import { getRoleColor, getRoleLabel, STATUS } from '@/constants';
import type { User } from '@/types/user';
import { normalizeImageSource } from '@/utils/imageSource';
import {
  Avatar,
  Card,
  Col,
  Divider,
  Empty,
  Image,
  Row,
  Space,
  Tag,
  Typography,
} from 'antd';
import React, { useEffect, useMemo, useState } from 'react';

const USER_STATUS_LABELS: Record<string, string> = {
  [STATUS.ACTIVE]: 'Activo',
  [STATUS.INACTIVE]: 'Inactivo',
  [STATUS.DELETED]: 'Eliminado',
};

const USER_STATUS_COLORS: Record<string, string> = {
  [STATUS.ACTIVE]: 'green',
  [STATUS.INACTIVE]: 'orange',
  [STATUS.DELETED]: 'red',
};

const fallbackValue = (value?: string | null) => {
  const trimmedValue = value?.trim();
  return trimmedValue && trimmedValue !== '' ? trimmedValue : '—';
};

type CurrentUserProfileViewProps = {
  currentUser?: User;
};

type DetailFieldProps = {
  label: string;
  value?: string | null;
};

const DetailField: React.FC<DetailFieldProps> = ({ label, value }) => (
  <Space direction="vertical" size={2} style={{ width: '100%' }}>
    <Typography.Text type="secondary">{label}</Typography.Text>
    <Typography.Text style={{ wordBreak: 'break-word' }}>
      {fallbackValue(value)}
    </Typography.Text>
  </Space>
);

const CurrentUserProfileView: React.FC<CurrentUserProfileViewProps> = ({
  currentUser,
}) => {
  const [avatarError, setAvatarError] = useState(false);

  const profile = currentUser?.profile;
  const displayName = useMemo(() => {
    const firstName = profile?.first_name?.trim() ?? '';
    const lastName = profile?.last_name?.trim() ?? '';
    const fullName = [firstName, lastName].filter(Boolean).join(' ');
    return fullName !== '' ? fullName : currentUser?.email ?? '—';
  }, [currentUser?.email, profile?.first_name, profile?.last_name]);

  const normalizedPhoto = normalizeImageSource(profile?.photo);
  const normalizedThumbnail = normalizeImageSource(profile?.thumbnail);
  const avatarSrc = avatarError
    ? undefined
    : normalizedThumbnail || normalizedPhoto || undefined;
  const previewImageSrc = avatarError
    ? undefined
    : normalizedPhoto || normalizedThumbnail || undefined;

  useEffect(() => {
    setAvatarError(false);
  }, [normalizedPhoto, normalizedThumbnail]);

  const statusLabel = USER_STATUS_LABELS[currentUser?.status ?? ''] ?? '—';
  const statusColor =
    USER_STATUS_COLORS[currentUser?.status ?? ''] ?? 'default';

  if (!currentUser) {
    return <Empty description="No hay datos de perfil disponibles" />;
  }

  return (
    <Card variant="outlined" size="small" styles={{ body: { padding: 16 } }}>
      <Space direction="vertical" size={16} style={{ width: '100%' }}>
        <Space align="start" size={16} style={{ width: '100%' }}>
          {avatarSrc ? (
            <Image
              alt={`Foto de perfil de ${displayName}`}
              src={avatarSrc}
              width={88}
              height={88}
              preview={{
                src: previewImageSrc,
                mask: 'Ver foto',
              }}
              style={{
                borderRadius: '50%',
                cursor: 'pointer',
                objectFit: 'cover',
              }}
              onError={() => {
                setAvatarError(true);
              }}
            />
          ) : (
            <Avatar size={88}>{displayName.charAt(0).toUpperCase()}</Avatar>
          )}
          <Space direction="vertical" size={6} style={{ minWidth: 0 }}>
            <Typography.Title
              level={5}
              style={{ margin: 0, lineHeight: 1.25, wordBreak: 'break-word' }}
            >
              {displayName}
            </Typography.Title>
            <Typography.Text
              type="secondary"
              style={{ wordBreak: 'break-word' }}
            >
              {currentUser.email}
            </Typography.Text>
            <Space wrap size={6}>
              <Tag color={getRoleColor(currentUser.role)}>
                {getRoleLabel(currentUser.role)}
              </Tag>
              <Tag color={statusColor}>{statusLabel}</Tag>
            </Space>
          </Space>
        </Space>

        <Divider style={{ margin: 0 }} />

        <Space direction="vertical" size={12} style={{ width: '100%' }}>
          <Typography.Text strong>Cuenta</Typography.Text>
          <Row gutter={[16, 12]}>
            <Col xs={24} md={12}>
              <DetailField label="Email" value={currentUser.email} />
            </Col>
            <Col xs={24} md={12}>
              <DetailField label="Rol" value={getRoleLabel(currentUser.role)} />
            </Col>
            <Col xs={24} md={12}>
              <DetailField label="Estado" value={statusLabel} />
            </Col>
            <Col xs={24} md={12}>
              <DetailField label="UUID" value={currentUser.uuid} />
            </Col>
          </Row>
        </Space>

        <Divider style={{ margin: 0 }} />

        <Space direction="vertical" size={12} style={{ width: '100%' }}>
          <Typography.Text strong>Información Personal</Typography.Text>
          <Row gutter={[16, 12]}>
            <Col xs={24} md={12}>
              <DetailField label="Nombre" value={profile?.first_name} />
            </Col>
            <Col xs={24} md={12}>
              <DetailField label="Apellido" value={profile?.last_name} />
            </Col>
            <Col xs={24} md={12}>
              <DetailField
                label="Número de documento"
                value={profile?.document_number}
              />
            </Col>
            <Col xs={24} md={12}>
              <DetailField label="Teléfono" value={profile?.phone_number} />
            </Col>
          </Row>
        </Space>
      </Space>
    </Card>
  );
};

export default CurrentUserProfileView;
